// ─── Vinyl Player Canvas Element ─────────────────────────────────
// Phase 1.9.17 — Studio + public now share the same CompactVinylPlayer
// so they cannot visually drift. The legacy hero-style VinylPlayer
// (used by the RSVPTemplates) is intentionally NOT imported here any
// more — that component still ships for templates that want the full
// expanded hero, but a free-form canvas widget must stay inside its
// saved bounds.

import React, { useContext } from 'react';
import { Volume2 } from 'lucide-react';
import CompactVinylPlayer from './CompactVinylPlayer';
import MinimalAudioButton from './MinimalAudioButton';
import { CollageEditContext } from '../CollageEditContext';

const API = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
}

export default function VinylElement({ element, isMobile, theme, isEditing }) {
  const editCtx = useContext(CollageEditContext);
  const inEditor = isEditing || !!editCtx;
  const {
    musicUrl = '',
    songTitle = 'Our Song',
    artistName = 'Wedding Soundtrack',
    coverImage = '',
    playerStyle = 'vinyl',
    accentColor = theme?.accent || '#2c241d',
    bgColor = theme?.surface || '#f8f3eb',
    customStyle = {},
  } = element;

  const resolvedAccent = customStyle.accentColor || accentColor;
  const resolvedBg = customStyle.bgColor || bgColor;
  const resolvedCover = resolveUrl(coverImage);
  const resolvedMusic = resolveUrl(musicUrl);

  // ── Minimal "launcher" style — small audio chip ──────────────────
  if (playerStyle === 'minimal') {
    if (inEditor) {
      return (
        <div className="w-full py-3 flex items-center justify-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
            style={{ backgroundColor: resolvedAccent }}
          >
            <Volume2 className="w-5 h-5" style={{ color: resolvedBg }} />
          </div>
          {songTitle && (
            <span className="ml-3 text-xs font-medium opacity-60" style={{ color: resolvedAccent }}>
              {songTitle}
            </span>
          )}
        </div>
      );
    }
    if (!musicUrl) return null;
    return (
      <MinimalAudioButton
        audioSrc={resolvedMusic}
        accentColor={resolvedAccent}
        bgColor={resolvedBg}
      />
    );
  }

  // ── Vinyl style — Studio + public share CompactVinylPlayer ────────
  // Both branches render the identical composition inside the saved
  // widget bounds. Play button stops propagation so Studio clicks on
  // the widget body still select the element, while the play affordance
  // remains available for designers to audition their music.
  return (
    <CompactVinylPlayer
      audioSrc={resolvedMusic}
      songTitle={songTitle}
      artistName={artistName}
      coverImage={resolvedCover}
      accentColor={resolvedAccent}
      bgColor={resolvedBg}
      discColor={element.discColor || '#000000'}
      labelColor={element.labelColor || '#efe5d6'}
      tonearmX={element.tonearmX ?? 50}
      tonearmY={element.tonearmY ?? -12}
      tonearmScale={element.tonearmScale ?? 125}
      tonearmColor={element.tonearmColor}
      interactive={!inEditor}
      testId={`vinyl-element-${element.id}`}
    />
  );
}
